import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated } from '../../services/authService';
import SEO from '../../components/common/SEO/SEO';
import { useSEO } from '../../hooks/useSEO';
import { useSEOOptimization } from '../../hooks/useSEOOptimization';
import { useGoogleAnalytics, usePageEngagement } from '../../hooks/useGoogleAnalytics';
import YouTubeEmbed from '../../components/YouTubeEmbedLite';
import OptimizedImage from '../../components/OptimizedImage/OptimizedImage';
import TimeCalculator from '../../components/TimeCalculator/TimeCalculator';
import styles from './Home.module.css';

// Chemins vers les images optimisées dans /public
const publishImage = '/images/features/publish.png';
const renewImage = '/images/features/renew.png';
const centralizeImage = '/images/features/centralize.png';
const exchangeImage = '/images/features/exchange.png';
const favoritesImage = '/images/features/favorites.png';
const autoMessageImage = '/images/features/autoMessage.png';
const aiAssistantImage = '/images/features/aiAssistant.png';
const imageEditorImage = '/images/features/imageEditor.png';
const autoDeleteImage = '/images/features/autoDelete.png';

const Home = () => {  const { t } = useTranslation();
  const isLoggedIn = isAuthenticated();
  
  // Analytics et engagement tracking
  const { trackButtonClick, trackConversion } = useGoogleAnalytics();
  usePageEngagement('home', 'landing_page');
    // SEO et optimisation - Mémorisation pour éviter les re-renders infinis
  const seoConfig = useMemo(() => ({
    title: t('home.seo.title', 'Gestion et Partage d\'Articles Simplifiés'),
    description: t('home.seo.description', 'FlowDino simplifie la gestion de vos articles. Publiez, partagez et gérez votre contenu efficacement avec notre plateforme intuitive.'),
    keywords: t('home.seo.keywords', 'FlowDino, gestion articles, publication, partage contenu, workflow, productivité'),
    type: 'website',
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "name": "FlowDino",
          "description": t('home.seo.description'),
          "url": process.env.REACT_APP_BASE_URL,
          "logo": `${process.env.REACT_APP_BASE_URL}/react/images/logo.png`,
          "sameAs": [
            "https://twitter.com/flowdino",
            "https://linkedin.com/company/flowdino"
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": (() => {
            const items = t('home.faq.items', { returnObjects: true });
            return Array.isArray(items) ? items.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": { "@type": "Answer", "text": item.answer }
            })) : [];
          })()
        }
      ]
    }
  }), [t]);
    const { seoData } = useSEO(seoConfig);useSEOOptimization();

  // Mémorisation des cartes de fonctionnalités pour éviter les re-renders
  const featureCards = useMemo(() => [
    {
      image: publishImage,
      titleKey: "home.features.cards.publish.title",
      descriptionKey: "home.features.cards.publish.description"
    },
    {
      image: renewImage,
      titleKey: "home.features.cards.renew.title",
      descriptionKey: "home.features.cards.renew.description"
    },
    {
      image: centralizeImage,
      titleKey: "home.features.cards.centralize.title",
      descriptionKey: "home.features.cards.centralize.description"
    },
    {
      image: exchangeImage,
      titleKey: "home.features.cards.exchange.title",
      descriptionKey: "home.features.cards.exchange.description"
    },
    {
      image: favoritesImage,
      titleKey: "home.features.cards.favorites.title",
      descriptionKey: "home.features.cards.favorites.description"
    },
    {
      image: imageEditorImage,
      titleKey: "home.features.cards.imageEditor.title",
      descriptionKey: "home.features.cards.imageEditor.description"
    },    
    {
      image: autoMessageImage,
      titleKey: "home.features.cards.autoMessage.title",
      descriptionKey: "home.features.cards.autoMessage.description"
    },    
    {
      image: aiAssistantImage,
      titleKey: "home.features.cards.aiAssistant.title",
      descriptionKey: "home.features.cards.aiAssistant.description"
    },
    {
      image: autoDeleteImage,
      titleKey: "home.features.cards.autoDelete.title",
      descriptionKey: "home.features.cards.autoDelete.description"
    }
  ], [
    publishImage, renewImage, centralizeImage, exchangeImage, 
    favoritesImage, autoMessageImage, aiAssistantImage, 
    imageEditorImage, autoDeleteImage
  ]);

  // Debug sécurisé (seulement en mode développement et une seule fois)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Images importées (Home.jsx):', {
        publishImage,
        renewImage,
        centralizeImage,
        exchangeImage,
        favoritesImage,
        autoMessageImage,
        aiAssistantImage,
        imageEditorImage,
        autoDeleteImage
      });
      console.log('featureCards length:', featureCards.length);
      console.log('featureCards:', featureCards);
    }  }, [featureCards]); // Dépendance sur featureCards pour debug

  return (
    <div className={styles.home}>
      <SEO {...seoData} />
            <div className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <div className={styles.heroTagline}>
              {t('home.hero.tagline')}
            </div>
            <h1>{t('home.hero.title')}</h1>
            <p>{t('home.hero.subtitle')}</p>

            <div className={styles.heroButtons}>
              {!isLoggedIn && (
                <Link to="/register" className={styles.primaryButton}>
                  {t('home.hero.cta')}
                </Link>
              )}
              <a 
                href="https://chromewebstore.google.com/detail/flowdino-extension/iealfioknccembacfpjgodhoehdmggak" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.extensionButton}
              >
                <svg 
                  className={styles.chromeIcon} 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
                {t('home.hero.downloadExtension', 'Télécharger sur Chrome Store')}
              </a>
            </div>
          </div>
          
          <div className={styles.heroRight}>
            <div className={styles.heroPlatformsGrid}>
              <div className={styles.heroPlatformCard}>
                <img src="https://www.flowdino.com/images/leboncoin.c5626136.svg" alt="Leboncoin" />
              </div>
              <div className={styles.heroPlatformCard}>
                <img src="https://www.flowdino.com/images/logoVinted.svg" alt="Vinted" />
              </div>
              <div className={styles.heroPlatformCard}>
                <img src="https://www.flowdino.com/images/Etsy-Logo.png" alt="Etsy" />
              </div>
              <div className={styles.heroPlatformCard}>
                <img src="https://www.flowdino.com/images/Ebay-Logo.png" alt="eBay" />
              </div>
              <div className={styles.heroPlatformCard}>
                <img src="https://www.flowdino.com/images/VestiaireCollective.png" alt="Vestiaire Collective" className={styles.logoVestiaire} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Vidéo de présentation */}
      <div className={styles.videoSection}>
        <div className={styles.container}>
          <div className={styles.videoLayout}>
            <div className={styles.videoLeft}>
              <div className={styles.videoContainer}>
                <YouTubeEmbed 
                  videoId="ud1bySTPL8k"
                  title="Présentation FlowDino"
                  preload={true}
                />
              </div>
            </div>
            
            <div className={styles.videoRight}>
              <h2>{t('home.video.title', 'Découvrez FlowDino en action')}</h2>
              <p className={styles.videoPitch}>
                {t('home.video.pitch', 'FlowDino révolutionne la gestion de vos annonces en ligne. Gagnez du temps et découplez la puissance de l\'automatisation.')}
              </p>
              
              <ul className={styles.videoFeatures}>
                <li>
                  <svg className={styles.checkIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('home.video.feature1', 'Publication automatique sur 5 plateformes majeures')}</span>
                </li>
                <li>
                  <svg className={styles.checkIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('home.video.feature2', 'Optimisation IA de vos descriptions')}</span>
                </li>
                <li>
                  <svg className={styles.checkIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('home.video.feature3', 'Gestion centralisée de tous vos articles')}</span>
                </li>
                <li>
                  <svg className={styles.checkIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('home.video.feature4', 'Renouvellement automatique de vos annonces')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.features}>
        <div className={styles.container}>
          <h2>{t('home.features.title')}</h2>
          <div className={styles.featureGrid}>
            {featureCards.map((card, index) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureImageContainer}>
                  <OptimizedImage 
                    src={card.image} 
                    alt={t(card.titleKey)} 
                    className={styles.featureImage}
                    sizes="(max-width: 480px) 40px, (max-width: 768px) 48px, 192px"
                  />
                </div>
                <div className={styles.featureContent}>
                  <h3>{t(card.titleKey)}</h3>
                  <p>{t(card.descriptionKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Comment ça marche */}
      <div className={styles.howItWorksSection}>
        <div className={styles.container}>
          <h2>{t('home.howItWorks.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('home.howItWorks.subtitle')}</p>
          <p className={styles.sectionIntro}>{t('home.howItWorks.intro')}</p>
          <div className={styles.stepsGrid}>
            {(Array.isArray(t('home.howItWorks.steps', { returnObjects: true })) ? t('home.howItWorks.steps', { returnObjects: true }) : []).map((step, i) => (
              <div key={i} className={styles.stepCard}>
                <div className={styles.stepNumber}>{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Cas d'usage */}
      <div className={styles.useCasesSection}>
        <div className={styles.container}>
          <h2>{t('home.useCases.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('home.useCases.subtitle')}</p>
          <div className={styles.useCasesGrid}>
            {(Array.isArray(t('home.useCases.items', { returnObjects: true })) ? t('home.useCases.items', { returnObjects: true }) : []).map((item, i) => (
              <div key={i} className={styles.useCaseCard}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Valeur ajoutée par plateforme */}
      <div className={styles.platformValueSection}>
        <div className={styles.container}>
          <h2>{t('home.platformValue.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('home.platformValue.subtitle')}</p>
          <div className={styles.platformValueGrid}>
            {(Array.isArray(t('home.platformValue.items', { returnObjects: true })) ? t('home.platformValue.items', { returnObjects: true }) : []).map((item, i) => (
              <div key={i} className={styles.platformValueCard}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Calculateur de temps */}
      <div className={styles.calculatorSection}>
        <div className={styles.container}>
          <TimeCalculator />
        </div>
      </div>

      {/* Section Essai gratuit 14 jours */}
      <div className={styles.freeTrialSection}>
        <div className={styles.container}>
          <div className={styles.freeTrialCard}>
            <div className={styles.freeTrialBadge}>{t('home.freeTrial.badge')}</div>
            <h2 className={styles.freeTrialTitle}>{t('home.freeTrial.title')}</h2>
            <p className={styles.freeTrialSubtitle}>
              {t('home.freeTrial.subtitle')}<br />
              {t('home.freeTrial.subtitleLine2')}
            </p>
            <Link
              to="/register"
              className={styles.freeTrialButton}
              onClick={() => trackConversion('free_trial_cta')}
            >
              {t('home.freeTrial.cta')}
            </Link>
            <p className={styles.freeTrialHint}>{t('home.freeTrial.alreadyMember')}&nbsp;? <Link to="/login" className={styles.freeTrialLoginLink}>{t('home.freeTrial.loginLink')}</Link></p>
          </div>
        </div>
      </div>

      {/* Section FAQ */}
      <div className={styles.faqSection}>
        <div className={styles.container}>
          <h2>{t('home.faq.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('home.faq.subtitle')}</p>
          <div className={styles.faqList}>
            {(Array.isArray(t('home.faq.items', { returnObjects: true })) ? t('home.faq.items', { returnObjects: true }) : []).map((item, i) => (
              <details key={i} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  <span>{item.question}</span>
                  <svg className={styles.faqChevron} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className={styles.faqAnswer}>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* Section Tutoriels Vidéo */}
      <div className={styles.tutorialsSection}>
        <div className={styles.container}>
          <h2>{t('home.tutorials.title', 'Tutoriels vidéo')}</h2>
          <p className={styles.tutorialsSubtitle}>{t('home.tutorials.subtitle', 'Découvrez comment utiliser FlowDino en quelques minutes')}</p>
          
          <div className={styles.tutorialsGrid}>
            <div className={styles.tutorialCard}>
              <div className={styles.tutorialVideoContainer}>
                <YouTubeEmbed 
                  videoId="FjM1bbO_2l8"
                  title="Comment publier avec FlowDino"
                />
              </div>
              <h3>{t('home.tutorials.video1.title', 'Comment publier avec FlowDino')}</h3>
              <p>{t('home.tutorials.video1.description', 'Apprenez à publier vos annonces en un clic sur toutes les plateformes')}</p>
            </div>
            
            <div className={styles.tutorialCard}>
              <div className={styles.tutorialVideoContainer}>
                <YouTubeEmbed 
                  videoId="kea3EcMKzBc"
                  title="Comment choisir les articles sur FlowDino"
                />
              </div>
              <h3>{t('home.tutorials.video2.title', 'Comment choisir les articles sur FlowDino')}</h3>
              <p>{t('home.tutorials.video2.description', 'Découvrez la page de sélection des articles')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default Home;
